import ObjectHash from 'object-hash'
import { Buffer } from 'buffer'

export const state = () => {
	return {
		challenges: [],
		next: null,
		ttl_challenges: null,
		cache_duration_challenges: 1800000, // 30 minutes
		cache_key_challenges: '',
		challenge: {},
		ttl_challenge: null,
		cache_duration_challenge: 1800000, // 30 minutes
		cache_key_challenge: '',
	}
}

function isInCacheChallenges(state, companyId, params) {
	if (state.cache_key_challenges !== buildCacheKeyChallenges(companyId, params)) {
		return false
	}
	if (state.ttl_challenges === null || state.ttl_challenges < Date.now()) {
		return false
	}
	return true
}

function buildCacheKeyChallenges(companyId, params) {
	return `company:${companyId}:challenges:${ObjectHash(params)}`
}

function isInCacheChallenge(state, challengeId) {
	if (state.cache_key_challenge !== buildCacheKeyChallenge(challengeId)) {
		return false
	}
	if (state.ttl_challenge === null || state.ttl_challenge < Date.now()) {
		return false
	}
	return true
}

function buildCacheKeyChallenge(challengeId) {
	return `challenge:${challengeId}`
}

export const actions = {
	async uploadExample(_, { companyId, extension, file_type: fileType, data }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/challenges/challenge-exemple-upload-url/`, {
				params: { extension },
			})
			.then(async (res) => {
				await $fetch(res.signed_url, {
					method: 'PUT',
					body: Buffer.from(data.replace(/^data:\w+\/\w+;base64,/, ''), 'base64'),
					headers: { 'Content-Type': fileType },
				})

				return res.remote_url
			})
	},

	async count(_, { companyId, params }) {
		let countUrl = `api/company/${companyId}/challenges/?limit=1`

		const { status, ...restParams } = params
		if (status) status.forEach((tc) => (countUrl += `&status[]=${tc}`))

		return await useNuxtApp()
			.$api(countUrl, { params: restParams })
			.then((res) => {
				return res.count
			})
	},

	async list({ state, commit }, { companyId, params }) {
		if (!params && isInCacheChallenges(state, companyId, params)) {
			return state.challenges
		}
		commit('clearNext')

		let listUrl = `api/company/${companyId}/challenges/`

		const { challenge_template_id: templateIds, status, ...restParams } = params

		if (templateIds) templateIds.forEach((sn) => (listUrl += `&challenge_template_id[]=${sn}`))
		if (status) status.forEach((tc) => (listUrl += `&status[]=${tc}`))

		listUrl = listUrl.replace('&', '?')

		return await useNuxtApp()
			.$api(listUrl, { params: restParams })
			.then((res) => {
				commit('setChallenges', { data: res, companyId, params })
				if (params.limit) {
					return res.results
				} else {
					return res
				}
			})
			.catch((err) => {
				commit('invalidateCacheChallenges')
				throw err
			})
	},

	async get_page({ commit, state }, { companyId }) {
		const next = state.next
		commit('clearNext')
		return await useNuxtApp()
			.$api(next)
			.then((res) => {
				commit('setChallengesPaginate', { data: res, companyId, params: { next } })
				return res
			})
	},

	async retrieve({ state, commit }, { companyId, challengeId }) {
		if (isInCacheChallenge(state, challengeId)) {
			return state.challenge
		}
		return await useNuxtApp()
			.$api(`api/company/${companyId}/challenges/${challengeId}/`)
			.then((res) => {
				commit('setChallenge', { data: res, challengeId })
				return res
			})
			.catch((err) => {
				commit('clearCacheChallenge')
				throw err
			})
	},

	async create({ commit }, { companyId, data }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/challenges/`, {
				method: 'POST',
				body: data,
			})
			.finally(() => {
				commit('invalidateCacheChallenges')
				commit('brandingChallenge/clearCachePreviewChallenges', null, { root: true })
				commit('programs/invalidateCachePrograms', null, { root: true })
				commit('programs/invalidateCacheProgram', null, { root: true })
			})
	},

	async partialUpdate({ commit }, { companyId, challengeId, data }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/challenges/${challengeId}/`, {
				method: 'PATCH',
				body: data,
			})
			.then((res) => commit('patchChallenge', { data: res, challengeId: res.id }))
			.finally(() => {
				commit('invalidateCacheChallenges')
				commit('brandingChallenge/clearCachePreviewChallenges', null, { root: true })
				commit('programs/invalidateCachePrograms', null, { root: true })
				commit('programs/clearCacheProgram', null, { root: true })
			})
	},

	async delete({ commit }, { companyId, challengeId }) {
		return await useNuxtApp()
			.$api(`api/company/${companyId}/challenges/${challengeId}/`, { method: 'DELETE' })
			.finally(() => {
				commit('invalidateCacheChallenges')
				commit('brandingChallenge/clearCachePreviewChallenges', null, { root: true })
				commit('programs/clearCachePrograms', null, { root: true })
				commit('programs/clearCacheProgram', null, { root: true })
				commit(
					'dashboardMissions/clearCacheGlobal',
					{ statKey: 'missions' },
					{ root: true },
				)
			})
	},

	async duplicate({ dispatch }, { companyId, challengeId }) {
		const existingChallenge = await dispatch('retrieve', { companyId, challengeId })

		let duplicatedData = { ...existingChallenge }
		delete duplicatedData.id
		duplicatedData.challenge_template = existingChallenge.challenge_template.id
		duplicatedData.name = `Clone : "${existingChallenge.name}"`.substring(0, 100)
		duplicatedData.draft = true

		// Remove properties with null values
		duplicatedData = Object.fromEntries(
			// eslint-disable-next-line no-unused-vars
			Object.entries(duplicatedData).filter(([_, value]) => value !== null),
		)

		const newChallenge = await dispatch('create', {
			companyId,
			data: duplicatedData,
		})

		return newChallenge
	},
}

export const mutations = {
	setChallenges(state, { data, companyId, params }) {
		if (params.limit) {
			state.challenges = data.results
			state.next = data.next
		} else {
			state.challenges = data
		}

		state.ttl_challenges = Date.now() + state.cache_duration_challenges
		state.cache_key_challenges = buildCacheKeyChallenges(companyId, params)
	},

	patchChallenge(state, { data, challengeId }) {
		const index = state.challenges.findIndex((challenge) => challenge.id === challengeId)
		if (index !== -1) {
			state.challenges[index] = data
		}
		state.ttl_challenges = null
	},

	setChallengesPaginate(state, { data, companyId, params }) {
		state.challenges = state.challenges.concat(data.results)
		state.next = data.next
		state.ttl_challenges = Date.now() + state.cache_duration_challenges
		state.cache_key_challenges = buildCacheKeyChallenges(companyId, params)
	},

	clearCacheChallenges(state) {
		state.challenges = []
		state.next = null
		state.ttl_challenges = null
		state.cache_key_challenges = ''
	},

	clearNext(state) {
		state.next = null
	},

	setChallenge(state, { data, challengeId }) {
		state.challenge = data
		state.ttl_challenge = Date.now() + state.cache_duration_challenge
		state.cache_key_challenge = buildCacheKeyChallenge(challengeId)
	},

	clearCacheChallenge(state) {
		state.challenge = {}
		state.ttl_challenge = null
		state.cache_key_challenge = ''
	},

	invalidateCacheChallenges(state) {
		state.ttl_challenges = null
	},

	invalidateCacheChallenge(state) {
		state.ttl_challenge = null
	},
}
